import { Injectable, NgZone, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Zoom général de l'application, indépendant du zoom navigateur. Appliqué via
 * `font-size` en % sur <html> (le classique "rem zoom") : la quasi-totalité
 * des tailles/espacements Tailwind sont en rem, donc tout suit.
 *
 * Deux approches plus "universelles" ont été essayées et écartées :
 * - `transform: scale()` casse le positionnement de tout ce qui est en
 *   `position: fixed` (modales, toasts, dropdowns ng-select en
 *   `appendTo="body"`, Le Deck), très présents dans l'app.
 * - la propriété CSS `zoom` semblait plus sûre sur ce point, mais désynchronise
 *   `vh`/`dvh` de `window.innerHeight` dans ce navigateur : la sidebar
 *   (`h-[100dvh]`) se met à rétrécir tandis que Le Deck et la messagerie
 *   (`position: fixed`, ancrés au vrai bas de fenêtre) restent à leur place —
 *   d'où le grand vide observé en bas d'écran une fois dézoomé.
 * `font-size` ne touche ni les unités de viewport ni le containing block des
 * éléments fixed : la coquille de l'appli (hauteur d'écran, éléments fixed)
 * reste intacte, seul le contenu en rem suit — au prix d'un léger residuel
 * de tailles fixes en px codées en dur (ex: `text-[11px]`) qui ne bougent pas.
 *
 * Volontairement local à cet ordinateur/navigateur (localStorage uniquement,
 * pas de sync via UserConfig comme FontService) : le zoom confortable dépend
 * de la taille et de la résolution de CET écran, pas d'une préférence
 * esthétique valable partout — synchroniser la valeur d'un grand moniteur vers
 * un laptop la rendrait illisible ou minuscule selon le sens.
 */
@Injectable({
  providedIn: 'root',
})
export class ZoomService {
  private readonly storageKey = 'app_zoom_level';
  private readonly defaultZoom = 100;
  private static readonly HUD_VISIBLE_MS = 7000;

  readonly minZoom = 60;
  readonly maxZoom = 140;
  readonly step = 5;

  private currentZoomSource = new BehaviorSubject<number>(this.getInitialZoom());
  currentZoom$ = this.currentZoomSource.asObservable();

  private readonly zone = inject(NgZone);

  constructor() {
    // Applique la valeur restaurée avant de brancher la transition : un
    // rechargement de page avec un zoom déjà personnalisé ne doit pas se voir
    // grandir/rétrécir à l'écran, seuls les changements décidés par
    // l'utilisateur ensuite doivent être animés.
    this.applyZoom(this.currentZoomSource.value);
    this.setupTransition();
    this.interceptBrowserZoomGestures();
  }

  getCurrentZoom(): number {
    return this.currentZoomSource.value;
  }

  setZoom(value: number): void {
    const next = this.normalize(value);
    if (next === this.currentZoomSource.value) return;

    this.currentZoomSource.next(next);
    this.applyZoom(next);
    localStorage.setItem(this.storageKey, String(next));
    this.showZoomHud(next);
  }

  /** Anime les changements de zoom décidés après coup (slider, boutons,
   * Ctrl+molette/±). Bref à dessein : `font-size` force un reflow de toute la
   * page à chaque frame, contrairement à un `transform` qui ne ferait que du
   * compositing — une transition longue serait sensible sur un drag rapide du
   * slider ou un Ctrl+molette soutenu. */
  private setupTransition(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.documentElement.style.transition = 'font-size 120ms ease-out';
  }

  private normalize(value: number): number {
    if (!Number.isFinite(value)) return this.defaultZoom;
    const stepped = Math.round(value / this.step) * this.step;
    return Math.min(this.maxZoom, Math.max(this.minZoom, stepped));
  }

  private getInitialZoom(): number {
    const stored = localStorage.getItem(this.storageKey);
    return this.normalize(Number(stored ?? this.defaultZoom));
  }

  private applyZoom(value: number): void {
    const root = document.documentElement;
    if (value === this.defaultZoom) {
      root.style.removeProperty('font-size');
    } else {
      root.style.setProperty('font-size', `${value}%`);
    }
  }

  private zoomHudEl: HTMLDivElement | null = null;
  private zoomHudHideTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Petit HUD flottant (façon indicateur volume/luminosité d'un OS) : affiche
   * le pourcentage courant en direct, quel que soit le point d'entrée (slider,
   * boutons, Ctrl+molette, Ctrl+/Ctrl-), avec l'avertissement en dessous — à
   * chaque changement, pas juste la première fois, sinon le rappel se perd la
   * fois où il compterait vraiment (un zoom poussé loin de 100%). Une seule
   * instance mise à jour en place (pas une par changement) pour rester lisible
   * pendant un drag de slider ou un Ctrl+molette soutenu.
   */
  private showZoomHud(value: number): void {
    if (typeof document === 'undefined') return;

    if (!this.zoomHudEl) {
      const el = document.createElement('div');
      el.className = 'app-zoom-hud';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.innerHTML = `
        <span class="app-zoom-hud__icon"><i class="fa-solid fa-magnifying-glass"></i></span>
        <span class="app-zoom-hud__text">
          <span class="app-zoom-hud__value"></span>
          <span class="app-zoom-hud__warning">Modifier le zoom peut provoquer un affichage temporairement aléatoire de certains éléments.</span>
        </span>
      `;
      document.body.appendChild(el);
      this.zoomHudEl = el;
    }

    const valueEl = this.zoomHudEl.querySelector<HTMLElement>('.app-zoom-hud__value');
    if (valueEl) valueEl.textContent = `${value}%`;

    requestAnimationFrame(() => this.zoomHudEl?.classList.add('app-zoom-hud--visible'));

    if (this.zoomHudHideTimer) clearTimeout(this.zoomHudHideTimer);
    this.zoomHudHideTimer = setTimeout(() => {
      this.zoomHudEl?.classList.remove('app-zoom-hud--visible');
      this.zoomHudHideTimer = null;
    }, ZoomService.HUD_VISIBLE_MS);
  }

  /**
   * Reprend Ctrl/Cmd+molette et Ctrl/Cmd + / - / 0 pour piloter CE zoom au
   * lieu du zoom natif du navigateur — cohérent avec le slider, et évite que
   * les deux zooms se cumulent sans qu'on comprenne pourquoi l'app a l'air
   * minuscule ou géante. Le zoom navigateur lui-même (menu, Ctrl+molette sur
   * un onglet sans focus app, raccourcis système) reste possible : rien côté
   * web ne permet de le désactiver complètement, et ce serait de toute façon
   * une régression d'accessibilité de l'empêcher.
   */
  private interceptBrowserZoomGestures(): void {
    if (typeof window === 'undefined') return;

    // Hors zone Angular : ces écouteurs globaux voient passer CHAQUE molette et CHAQUE touche
    // de l'application. En zone, chacun relançait une détection de changements de toute l'app
    // (~70 ms sur une page chargée) : défilement saccadé partout, éditeurs compris.
    // On ne rentre dans la zone que pour un vrai geste de zoom.
    this.zone.runOutsideAngular(() => {
      window.addEventListener('wheel', (event: WheelEvent) => {
        if (!event.ctrlKey && !event.metaKey) return;
        event.preventDefault();
        const direction = event.deltaY > 0 ? -1 : 1;
        this.zone.run(() => this.setZoom(this.getCurrentZoom() + direction * this.step));
      }, { passive: false });

      window.addEventListener('keydown', (event: KeyboardEvent) => this.surToucheZoom(event));
    });
  }

  private surToucheZoom(event: KeyboardEvent): void {
    if (!event.ctrlKey && !event.metaKey) return;
    this.zone.run(() => {
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        this.setZoom(this.getCurrentZoom() + this.step);
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        this.setZoom(this.getCurrentZoom() - this.step);
      } else if (event.key === '0') {
        event.preventDefault();
        this.setZoom(this.defaultZoom);
      }
    });
  }
}
